import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';
import { createContext } from '@shared/utils/createContext';
import { withWindow } from '@shared/utils/withWindow';
import usePersistedState from '@shared/hooks/usePersistedState';

type SmartAppBannerProviderContext = {
  isBannerVisible: boolean;
  updateBannerVisibility: (isVisible: boolean) => void;
  smartAppBannerHeight: number;
  calculateBannerHeight: () => void;
};

const [Provider, useSmartAppBannerProvider] = createContext<SmartAppBannerProviderContext>({
  name: 'SmartAppBannerProvider',
});

const SMART_APP_BANNER_VISIBILITY_KEY = 'smartAppBannerVisibility';

const visibilityOptions = {
  serialize: (visible: boolean) => (visible ? 'visible' : 'hidden'),
  deserialize: (raw: string): boolean | undefined => (raw === 'hidden' ? false : true),
} as const;

const SmartAppBannerProvider: FC = ({ children }) => {
  const [isBannerVisible, setIsBannerVisible] = usePersistedState(SMART_APP_BANNER_VISIBILITY_KEY, true, visibilityOptions);
  const [smartAppBannerHeight, setSmartAppBannerHeight] = React.useState(0);

  const calculateBannerHeight = useCallback(() => {
    const smartAppBanner = withWindow(() => document.getElementById('smart-app-banner'), null);
    const height = smartAppBanner?.clientHeight || 0;
    setSmartAppBannerHeight(height);
  }, []);

  useEffect(() => {
    calculateBannerHeight();
    withWindow(window => {
      window.addEventListener('resize', calculateBannerHeight);
    });
    return () => {
      withWindow(window => {
        window.removeEventListener('resize', calculateBannerHeight);
      });
    };
  }, [calculateBannerHeight]);

  return <Provider value={{ isBannerVisible, updateBannerVisibility: setIsBannerVisible, smartAppBannerHeight, calculateBannerHeight }}>{children}</Provider>;
};

export { SmartAppBannerProvider, useSmartAppBannerProvider };
